import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import { includes } from 'lodash';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { fetchClubCalendar } from '../../store/calendar/actions';
import { getCalendar, getLoadingError, hasLoaded, isLoading } from '../../store/calendar/selectors';
import { withTranslation } from '../../lib/i18n';
import Calendar from '../../components/calendar';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';

class CalendarPage extends React.Component {
  static async getInitialProps({ club, isServer, query, reduxStore, req, res }) {
    const {
      year: queryYear,
      month: queryMonth,
      eventType: queryEventType,
      teamId: queryTeamId,
      viewMode: queryViewMode,
    } = query;
    const now = DateTime.local();
    const year = queryYear ? parseInt(queryYear, 10) : now.year;
    const month = queryMonth ? parseInt(queryMonth, 10) : now.month;
    const showCompleteWeeks = 1;
    const teamId = queryTeamId ? parseInt(queryTeamId, 10) : null;
    const viewMode = queryViewMode === 'list' ? queryViewMode : 'grid';
    const eventType =
      queryEventType && includes(['events', 'fixtures', 'training'], queryEventType)
        ? queryEventType
        : null;
    const initialProps = {
      activeSection: 'news',
      namespacesRequired: ['common'],
      pageType: 'calendar.index',
      year,
      month,
      eventType,
      teamId,
      viewMode,
      showCompleteWeeks,
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(
        fetchClubCalendar({ clubId: club.id, year, month, request: req, showCompleteWeeks }),
      );
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate() {
    this.loadIfRequired();
  }

  loadIfRequired = () => {
    const { hasLoadedCalendar, isLoadingCalendar, loadCalendar, loadingError } = this.props;
    if (!hasLoadedCalendar && !isLoadingCalendar && !loadingError) {
      const {
        club: { id: clubId },
        year,
        month,
        showCompleteWeeks,
      } = this.props;
      loadCalendar({ clubId, year, month, showCompleteWeeks });
    }
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const { calendar, eventType, isLoadingCalendar, month, teamId, year, t, viewMode } = this.props;
    return (
      <Calendar
        calendar={calendar}
        club={club}
        eventType={eventType}
        isLoading={isLoadingCalendar}
        month={month}
        teamId={teamId}
        year={year}
        viewMode={viewMode}
        t={t}
      />
    );
  }
}

CalendarPage.defaultProps = {
  calendar: null,
  eventType: null,
  loadingError: null,
  teamId: null,
};

CalendarPage.propTypes = {
  calendar: PropTypes.shape(),
  club: CLUB_PROP_TYPES.isRequired,
  eventType: PropTypes.oneOf(['events', 'fixtures', 'training']),
  hasLoadedCalendar: PropTypes.bool.isRequired,
  isLoadingCalendar: PropTypes.bool.isRequired,
  loadCalendar: PropTypes.func.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  month: PropTypes.number.isRequired,
  teamId: PropTypes.number,
  year: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  viewMode: PropTypes.oneOf(['grid', 'list']).isRequired,
  showCompleteWeeks: PropTypes.oneOf([0, 1]).isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  calendar: getCalendar(
    state,
    ownProps.club.id,
    ownProps.year,
    ownProps.month,
    ownProps.teamId,
    ownProps.eventType,
  ),
  isLoadingCalendar: isLoading(state),
  hasLoadedCalendar: hasLoaded(state, ownProps.club.id, ownProps.year, ownProps.month),
  loadingError: getLoadingError(state),
});

const dispatchToProps = {
  loadCalendar: fetchClubCalendar,
};

export default connect(mapStateToProps, dispatchToProps)(withTranslation(['common'])(CalendarPage));
